<template>
    <div class="gm-page pa-4">
        <!-- 顶部三栏：左-欢迎+时间 | 中-KPI | 右-快捷入口 -->
        <PageTop :menus="['收益总览', '电价录入', '抄表结算']" />
        <!-- 主体 -->
        <div class="ops-content">
            <v-row dense class="ops-row">
                <!-- 左侧 -->
                <v-col cols="12" lg="9" class="ops-col-left">
                    <!-- 基础数据统计（设计图：卡片容器 + 标题 + 6 项，数值蓝色） -->
                    <div class="ops-basic-section ops-basic-bg" v-loading="loadingBasicData">
                        <h3 class="ops-basic-title">基础数据统计</h3>
                        <div class="ops-cards">
                            <div v-for="c in basicCards" :key="c.key" class="ops-card">
                                <img :src="require(`@/assets/${c.icon}`)" />

                                <div class="ops-card-label">{{ c.label }}</div>
                                <div class="ops-card-value-wrap">
                                    <span class="ops-card-value">{{ c.value }}</span>
                                    <span class="ops-card-unit">{{ c.unit }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 收益列表（设计图：标题+搜索+筛选+表格+分页） -->
                    <v-card class="ops-card-panel ops-revenue-panel ops-basic-bg" rounded="xl" elevation="0">
                        <div class="ops-revenue-header">
                            <h3 class="ops-panel-title">收益列表</h3>
                            <div class="ops-revenue-toolbar">
                                <el-input v-model="revenueSearch" placeholder="输入电站名称或id搜索" clearable class="ops-search"
                                    :suffix-icon="Search" @input="onRevenueSearchInput"
                                    @keyup.enter="onRevenueSearchEnter" @clear="onRevenueSearchClear" />
                                <div class="ops-filter-tabs">
                                    <span v-for="f in filterTabs" :key="f.value" class="ops-filter-tab"
                                        :class="{ active: revenueFilter === f.value }"
                                        @click="revenueFilter = f.value">{{ f.label }}</span>
                                </div>
                                <el-select v-if="showRevenueFilterSelect" v-model="revenueFilterValue"
                                    class="ops-filter-dropdown" clearable :placeholder="revenueFilterPlaceholder">
                                    <el-option v-for="opt in revenueFilterOptions" :key="opt.value" :label="opt.label"
                                        :value="opt.value" />
                                </el-select>
                            </div>
                        </div>
                        <div class="ops-table-wrap" v-loading="loadingRevenueList">
                            <div class="ops-revenue-table-inner" ref="revenueTableInnerRef">
                                <v-table density="compact"
                                    class="bg-transparent flex-grow-1 gm-table ops-table ops-revenue-table"
                                    style="width: 100%">
                                    <thead>
                                        <tr>
                                            <th style="width: 180px">电站名称</th>
                                            <th style="width: 140px">所属省市</th>
                                            <th class="text-end" style="width: 160px">昨日收益</th>
                                            <th style="width: 160px">昨日充放</th>
                                            <th class="text-end" style="width: 90px">度电收益</th>
                                            <th class="text-end" style="width: 100px">昨日效率</th>
                                            <th class="text-end" style="width: 80px">循环次数</th>
                                            <th style="width: 100px">收入类型</th>
                                            <th style="width: 80px">设备类型</th>
                                            <th class="text-center" style="width: 120px">日报详情</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(row, idx) in revenueList" :key="row.stationId ?? row.id ?? idx">
                                            <td class="ops-station-cell">
                                                <div class="ops-station-name">
                                                    <router-link :to="stationDetailPath(row)" class="ops-station-link"
                                                        :title="row.name != null && row.name !== '' ? String(row.name) : undefined">{{
                                                            row.name }}</router-link>
                                                    <div class="ops-station-spec">
                                                        <span>{{ formatPowerNumberMW(row.maxPower).value }}</span>
                                                        <span>{{ formatPowerNumberMW(row.maxPower).unit }}</span>
                                                        <span> / </span>
                                                        <span>{{ formatNumberMW(row.capacity).value }}</span>
                                                        <span>{{ formatNumberMW(row.capacity).unit }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span>{{ row.province + '-' + row.city }}</span>
                                            </td>
                                            <td class="text-end">
                                                <div>{{ fmtIncome(row.yesterdayIncome) }}元</div>
                                                <div v-if="row.incomeTip"
                                                    :class="['ops-tip', row.incomeTipDir === 'down' ? 'ops-tip-down' : '']">
                                                    <i class="ops-tip-arrow"></i>{{ row.incomeTip }}
                                                </div>
                                            </td>
                                            <td>
                                                <div class="ops-charge-discharge">充电量: {{
                                                    formatNumber(row.yesterdayPositive).value +
                                                    formatNumber(row.yesterdayPositive).unit }}</div>
                                                <div class="ops-charge-discharge">放电量: {{
                                                    formatNumber(row.yesterdayReverse).value +
                                                    formatNumber(row.yesterdayReverse).unit }}</div>
                                            </td>
                                            <td class="text-end">
                                                <span>{{ formatMoney(row.yesterdayIncome / row.capacity).value +
                                                    formatMoney(row.yesterdayIncome / row.capacity).unit }}</span>
                                            </td>
                                            <td class="text-end">
                                                <div>{{ fetchPower(row.yesterdayPositive, row.yesterdayReverse)
                                                }}</div>
                                                <div v-if="row.effTip"
                                                    :class="['ops-tip', row.effTipDir === 'down' ? 'ops-tip-down' : '']">
                                                    <i class="ops-tip-arrow"></i>{{ row.effTip }}
                                                </div>
                                            </td>
                                            <td class="text-end">
                                                {{ fetchCycleCount(row.yesterdayPositive, row.capacity) }}
                                                <span class="unit-text">次</span>
                                            </td>
                                            <td>
                                                <div class="ops-tags-wrap">
                                                    <span class="ops-tag income-blue">{{ '峰谷套利' }}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div class="ops-tags-wrap ops-device-tags">
                                                    <span class="ops-tag-device income-blue">{{ '储能' }}</span>
                                                </div>
                                            </td>
                                            <td class="text-center">
                                                <el-button type="primary" link size="small" class="ops-detail-link"
                                                    @click="openDayDetail(row)">详情</el-button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-table>
                            </div>
                        </div>
                        <div class="pagination-container ops-revenue-pagination">
                            <div class="pagination-info">共 {{ revenueTotal }} 项数据</div>
                            <div class="pagination-controls">

                                <v-pagination v-model="revenuePage" :length="totalPages" :total-visible="7"
                                    rounded="circle" size="small" @update:model-value="onRevenuePageChange" />

                            </div>
                        </div>
                    </v-card>
                </v-col>

                <!-- 右侧 -->
                <v-col cols="12" lg="3" class="ops-col-right">
                    <!-- 效率：背景条 + 三色块 + 边界气泡百分比 + 下方数量 -->
                    <v-card class="ops-right-card ops-basic-bg" rounded="xl" elevation="0" v-loading="loadingChartData">
                        <div class="ops-right-title">效率</div>
                        <div class="ops-efficiency-bars">
                            <div class="ops-eff-bar">
                                <div class="ops-eff-track">
                                    <!-- 背景条 -->
                                    <div class="ops-eff-bg"></div>
                                    <!-- 前景三色块，按 10 / 30 / 5 比例 -->
                                    <div class="ops-eff-colors">
                                        <div class="ops-eff-fill yellow" style="flex: 30;"></div>
                                        <div class="ops-eff-fill green" style="flex: 30;"></div>
                                        <div class="ops-eff-fill blue" style="flex: 30;"></div>
                                    </div>
                                    <!-- 85% 与 90% 气泡，定位在绿色起始与终止位置 -->
                                    <div class="ops-eff-bubble ops-eff-bubble-left">85%</div>
                                    <div class="ops-eff-bubble ops-eff-bubble-right">90%</div>
                                </div>
                                <div class="ops-eff-count-row">
                                    <span>{{ efficiencyData.low }}个</span>
                                    <span>{{ efficiencyData.normal }}个</span>
                                    <span>{{ efficiencyData.high }}个</span>
                                </div>
                            </div>
                        </div>
                    </v-card>

                    <!-- 收益分成：环形图 + 右侧图例（设计图配色与顺序） -->
                    <v-card class="ops-right-card ops-basic-bg" rounded="xl" elevation="0" v-loading="loadingChartData">
                        <div class="ops-right-title">收益分布</div>
                        <div class="ops-donut-row">
                            <div class="ops-donut-wrap">
                                <VChart class="ops-donut-chart" :option="revenueDonutOption" autoresize />
                            </div>
                            <div class="ops-donut-legend">
                                <div v-for="item in revenueDonutData" :key="item.name" class="ops-donut-item">
                                    <span class="ops-donut-dot" :style="{ background: item.color }"></span>
                                    <span class="ops-donut-name">{{ item.name }}</span>
                                    <span class="ops-donut-count">{{ item.value }}个</span>
                                    <span class="ops-donut-pct">{{ item.pct }}%</span>
                                </div>
                            </div>
                        </div>
                    </v-card>

                    <!-- 收益 柱状图 -->
                    <v-card class="ops-right-card ops-basic-bg" rounded="xl" elevation="0" v-loading="loadingChartData">
                        <div class="ops-right-title">收益</div>
                        <div class="ops-chart-box">
                            <VChart class="ops-chart" :option="revenueBarOption" autoresize />
                        </div>
                    </v-card>

                    <!-- 电量 堆叠柱状图 -->
                    <v-card class="ops-right-card ops-basic-bg" rounded="xl" elevation="0" v-loading="loadingChartData"
                        style="height:339px !important">
                        <div class="ops-right-title">电量</div>
                        <div class="ops-chart-box">
                            <VChart class="ops-chart" :option="electricityStackOption" autoresize />
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </div>



    <!-- 日报详情弹窗 -->
    <DayDetail v-model:visible="dayDetailVisible" :row="dayDetailRow" />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, inject, nextTick } from 'vue'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import PageTop from '@/components/PageTop/index.vue'

dayjs.extend(isoWeek)
import { Search } from '@element-plus/icons-vue'
import { getStationStatistics, getMonthIncomeAndPower, getStationList } from '@/api/workbench/index.js'
import { stationSearchNameOrId } from '@/utils/stationSearchQuery.js'
import { fmtIncome } from '@/utils/fortmatter.js'

import DayDetail from './components/dayDetail.vue'

const TIME_FMT = 'YYYY-MM-DD HH:mm:ss'

/** 按 Tab 生成接口所需的开始、结束时间 */
function getIncomeQueryTimeRange(range) {
    if (range === 'yesterday') {
        const d = dayjs().subtract(1, 'day')
        return {
            startTime: d.startOf('day').format(TIME_FMT),
            endTime: d.endOf('day').format(TIME_FMT)
        }
    }
    if (range === 'week') {
        return {
            startTime: dayjs().startOf('isoWeek').format(TIME_FMT),
            endTime: dayjs().endOf('isoWeek').format(TIME_FMT)
        }
    }
    if (range === 'month') {
        return {
            startTime: dayjs().startOf('month').format(TIME_FMT),
            endTime: dayjs().endOf('month').format(TIME_FMT)
        }
    }
    const d = dayjs().subtract(1, 'day')
    return {
        startTime: d.startOf('day').format(TIME_FMT),
        endTime: d.endOf('day').format(TIME_FMT)
    }
}

const timeRange = ref('yesterday')
const loadingBasicData = ref(false)
const loadingRevenueList = ref(false)
const loadingChartData = ref(false)
const loadingTopMetrics = ref(false)

/**
 * 按当前 timeRange 计算时间，并串行请求所有依赖该时间段的接口（保证用的是切换后的 start/end）
 */
async function refreshDataByTimeRange() {
    const range = getIncomeQueryTimeRange(timeRange.value)
    revenuePage.value = 1
    await Promise.all([
        fetchIncomAndPower(range),
        fetchStationList(range)
    ])
}

function onRevenuePageChange(newPage) {
    revenuePage.value = newPage
    fetchStationList(getIncomeQueryTimeRange(timeRange.value), newPage)
}

const REVENUE_SEARCH_DEBOUNCE_MS = 300
const REVENUE_SEARCH_THROTTLE_MS = 500
let revenueSearchDebounceTimer = null
let revenueSearchLastFetchAt = 0

function execRevenueSearchFetch({ useThrottle = false } = {}) {
    const now = Date.now()
    if (useThrottle && now - revenueSearchLastFetchAt < REVENUE_SEARCH_THROTTLE_MS) return

    revenuePage.value = 1
    fetchStationList(getIncomeQueryTimeRange(timeRange.value), 1, revenuePageSize.value)
    revenueSearchLastFetchAt = now
}

function onRevenueSearchInput() {
    if (revenueSearchDebounceTimer) {
        clearTimeout(revenueSearchDebounceTimer)
        revenueSearchDebounceTimer = null
    }
    revenueSearchDebounceTimer = setTimeout(() => {
        execRevenueSearchFetch()
        revenueSearchDebounceTimer = null
    }, REVENUE_SEARCH_DEBOUNCE_MS)
}

function onRevenueSearchEnter() {
    if (revenueSearchDebounceTimer) {
        clearTimeout(revenueSearchDebounceTimer)
        revenueSearchDebounceTimer = null
    }
    execRevenueSearchFetch({ useThrottle: true })
}

function onRevenueSearchClear() {
    // 点击 el-input 的 X 清空后，刷新列表（从第一页开始）
    if (revenueSearchDebounceTimer) {
        clearTimeout(revenueSearchDebounceTimer)
        revenueSearchDebounceTimer = null
    }
    execRevenueSearchFetch()
}

const topMetrics = ref([
    { key: 'stations', label: '总电站数', value: 0, unit: '' },
    { key: 'capacity', label: '累计装机容量GWH', value: 0, unit: '' },
    { key: 'safety', label: '安全天数', value: 0, unit: '' }
])

const basicCards = ref([
    { key: 'yesterdayIncomeTotal', label: '昨日收益', value: '0', unit: '元', icon: 'income.png' },
    { key: 'sum_income', label: '累计收益', value: '0', unit: '元', icon: 'income_all.png' },
    { key: 'yesterdayPositiveTotal', label: '昨日充电', value: '0', unit: 'kWh', icon: 'charge_icon.png' },
    { key: 'yesterdayReverseTotal', label: '昨日放电', value: '0', unit: 'kWh', icon: 'discharge_icon.png' },
    { key: 'sum_positive', label: '累计充电', value: '0', unit: 'kWh', icon: 'charge_all.png' },
    { key: 'sum_reverse', label: '累计放电', value: '0', unit: 'kWh', icon: 'discharge_all.png' }
])

/** 全部：不展示二级下拉、请求不带 area/容量/类型筛选 */
const revenueFilter = ref('all')
const revenueFilterValue = ref(null)

/** 仅「按类型 / 按区域 / 按容量」需要下拉；「全部」等不展示 */
const showRevenueFilterSelect = computed(() =>
    ['type', 'area', 'capacity'].includes(revenueFilter.value)
)

const revenueFilterPlaceholder = computed(() => {
    if (revenueFilter.value === 'type') return '选择收益类型'
    if (revenueFilter.value === 'capacity') return '选择容量区间'
    if (revenueFilter.value === 'area') return '选择区域'
    return '请选择'
})

/** 按容量筛选：与「我的电站」容量区间一致，传给接口 startCapacity / endCapacity */
const capacityFilterOptions = [
    { label: '<1MWh', value: { start: null, end: 1000 } },
    { label: '1-5MWh', value: { start: 1000, end: 5000 } },
    { label: '5-10MWh', value: { start: 5000, end: 10000 } },
    { label: '10-20MWh', value: { start: 10000, end: 20000 } },
    { label: '>20MWh', value: { start: 20000, end: null } }
]

const revenueFilterOptions = computed(() => {
    if (revenueFilter.value === 'type') {
        return [
            { label: '峰谷套利', value: 1 },
            { label: '电力现货', value: 2 },
            { label: '虚拟电厂', value: 3 },
            { label: '其他来源', value: 4 }
        ]
    }
    if (revenueFilter.value === 'capacity') {
        return capacityFilterOptions.map((o) => ({
            label: o.label,
            value: `${o.value.start}-${o.value.end}`
        }))
    }
    if (revenueFilter.value === 'area') {
        // 按“东北/华东/华中/华南/西南/西北”等区分区域
        const values = ['东北', '华东', '华中', '华南', '西南', '西北']
        return values.map(v => ({ label: v, value: v }))
    }
    return []
})

const filterTabs = [
    { value: 'all', label: '全部' },
    { value: 'area', label: '按区域' },
    { value: 'capacity', label: '按容量' }
]
const revenueSearch = ref('')
const revenuePage = ref(1)
const revenuePageSize = ref(14)
const revenueTotal = ref(0)
const revenueTableInnerRef = ref(null)
let revenueTableResizeObserver = null
let revenueAutoAdjustPending = false

function computeRevenuePageSize() {
    const inner = revenueTableInnerRef.value
    if (!inner) return null
    const wrapper = inner.querySelector('.v-table__wrapper') || inner
    const wrapperHeight = wrapper.clientHeight || inner.clientHeight
    if (wrapperHeight <= 0) return null

    const headerEl = wrapper.querySelector('thead')
    const headerHeight = headerEl ? headerEl.offsetHeight : 40
    const firstRow = wrapper.querySelector('tbody tr')
    const rowHeight = firstRow ? firstRow.offsetHeight : 60
    if (rowHeight <= 0) return null

    const available = wrapperHeight - headerHeight
    return Math.max(1, Math.floor(available / rowHeight))
}

async function autoAdjustRevenuePageSize() {
    if (revenueAutoAdjustPending) return
    revenueAutoAdjustPending = true
    try {
        await nextTick()
        const newSize = computeRevenuePageSize()
        if (!newSize || newSize === revenuePageSize.value) return
        revenuePageSize.value = newSize
        revenuePage.value = 1
        await fetchStationList(getIncomeQueryTimeRange(timeRange.value), 1, newSize)
    } finally {
        revenueAutoAdjustPending = false
    }
}

// 分页（参考 ChargeRecord.vue：顶部条 + 下拉分页 + 跳转页）
const jumpPage = ref(1)
const totalPages = computed(() => {
    const pages = Math.ceil(revenueTotal.value / revenuePageSize.value)
    return pages === 0 ? 1 : pages
})

watch(totalPages, (tp) => {
    if (jumpPage.value > tp) jumpPage.value = tp
})

const dayDetailVisible = ref(false)
const dayDetailRow = ref()
function openDayDetail(row) {
    dayDetailRow.value = row
    dayDetailVisible.value = true
}

// 根据“按类型/按容量/按区域 + 下拉值”刷新收益列表
const refreshRevenueListByFilters = async () => {
    revenuePage.value = 1
    await fetchStationList(getIncomeQueryTimeRange(timeRange.value))
}

let isResettingRevenueFilter = false
watch(
    revenueFilter,
    async () => {
        isResettingRevenueFilter = true
        revenueFilterValue.value = null
        await refreshRevenueListByFilters()
        isResettingRevenueFilter = false
    }
)

watch(revenueFilterValue, async () => {
    if (isResettingRevenueFilter) return
    await refreshRevenueListByFilters()
})


// 跳转单站视角（使用 path 确保在 hash 模式下也能正确跳转）
function stationDetailPath(row) {
    const id = (row && (row.stationId ?? row.id)) || '1'
    return { path: '/business-operations/detail', query: { stationId: id } }
}

// 收益列表：电站名拆为主名+规格，收入/设备支持多 tag（设计图）
const revenueList = ref([])

// 收益分成：接口 income 键 1~4 与下列顺序一一对应
const REVENUE_DONUT_DEF = [
    { incomeKey: 1, name: '峰谷套利', color: '#FF5227' },
    { incomeKey: 2, name: '电力现货', color: '#F3C209' },
    { incomeKey: 3, name: '虚拟电厂', color: '#00D90B' },
    { incomeKey: 4, name: '其他来源', color: '#3A68FF' }
]

/** 根据接口 income 对象生成环形图数据（value + 占比 pct） */
function buildRevenueDonutFromIncome(income) {
    const num = (k) => {
        const v = Number(income?.[k] ?? income?.[String(k)])
        return Number.isFinite(v) && v >= 0 ? v : 0
    }
    const rows = REVENUE_DONUT_DEF.map(({ incomeKey, name, color }) => ({
        name,
        color,
        value: num(incomeKey)
    }))
    const total = rows.reduce((s, r) => s + r.value, 0)
    return rows.map((r) => ({
        ...r,
        pct: total > 0 ? Math.round((r.value / total) * 1000) / 10 : 0
    }))
}

const revenueDonutData = ref(buildRevenueDonutFromIncome({}))
const revenueDonutOption = computed(() => ({
    tooltip: { trigger: 'item', formatter: '{b}: {c}%' },
    series: [
        {
            type: 'pie',
            radius: ["78%", "82%"],
            center: ['50%', '50%'],
            silent: true,
            hoverAnimation: false,
            tooltip: { show: false },
            label: { show: false },
            labelLine: {
                normal: {
                    show: false,
                    length: 30,
                    length2: 55
                },
                emphasis: {
                    show: false
                }
            },
            data: [{
                name: '',
                value: 0,
                itemStyle: {
                    normal: {
                        color: "#E1E3FF"
                    }
                }
            }]
        },
        {
            type: 'pie',
            radius: ['50%', '72%'],
            center: ['50%', '50%'],
            label: { show: true, position: 'outside', formatter: '{d}%', fontSize: 12 },
            labelLine: { show: true, length: 8, length2: 6 },
            data: revenueDonutData.value.map(d => ({ value: d.pct, name: d.name, itemStyle: { color: d.color } }))
        }
    ]
}))

// 收益柱状图：设计图 X 为 2024-08/2024-11/2025-02/2025-05，每类两根柱，Y 10000～40000
const revenueXDate = ref([])
const revenueBarData = ref([])

/** 本年 1 月到当前月，用于“无数据”时的 X 轴占位 */
const yearMonthsToNow = computed(() => {
    const now = dayjs()
    const y = now.year()
    const m = now.month() + 1 // 1-12
    return Array.from({ length: m }, (_, i) => `${y}-${String(i + 1).padStart(2, '0')}`)
})

/** 收益图：元 / 万元 / 亿元（与 workbench/OP 一致） */
function pickRevenueChartScale(maxAbsYuan) {
    const n = Math.abs(Number(maxAbsYuan)) || 0
    if (n >= 1e8) return { divisor: 1e8, unitName: '亿元' }
    if (n >= 1e4) return { divisor: 1e4, unitName: '万元' }
    return { divisor: 1, unitName: '元' }
}

const KWH_PER_MWH = 1000
const KWH_PER_GWH = 1_000_000

/** 电量图：kWh / MWh / GWh（与 workbench/OP 一致） */
function pickChargeDischargeEnergyScale(maxAbsKwh) {
    const n = Math.abs(Number(maxAbsKwh)) || 0
    if (n >= KWH_PER_GWH) return { divisor: KWH_PER_GWH, unit: 'GWh' }
    if (n >= KWH_PER_MWH) return { divisor: KWH_PER_MWH, unit: 'MWh' }
    return { divisor: 1, unit: 'kWh' }
}

const revenueBarOption = computed(() => {
    const hasData =
        Array.isArray(revenueXDate.value) &&
        revenueXDate.value.length > 0 &&
        Array.isArray(revenueBarData.value) &&
        revenueBarData.value.length > 0

    const grid = { top: 52, bottom: 16, left: 32, right: 16, containLabel: true }
    const axisLabel = { fontSize: 10, color: 'rgba(46,46,46,0.6)' }
    const xData = hasData ? revenueXDate.value : yearMonthsToNow.value

    if (!hasData) {
        return {
            grid,
            legend: { show: true, top: 0, right: 0, data: ['收益'] },
            xAxis: {
                type: 'category',
                data: xData,
                axisLabel,
                axisLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.12)' } },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                name: '收益(元)',
                nameLocation: 'end',
                nameTextStyle: {
                    color: 'rgba(46,46,46,0.6)',
                    fontSize: 10,
                    padding: [0, 0, 0, 0]
                },
                axisLabel: { fontSize: 10 },
                splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.06)' } },
                axisLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.12)' } },
                axisTick: { show: false }
            },
            tooltip: { show: false },
            series: []
        }
    }

    const rawIncome = revenueBarData.value || []
    const incomeNums = rawIncome.map((v) => Number(v)).filter((v) => Number.isFinite(v))
    const maxIncomeYuan = incomeNums.length ? Math.max(...incomeNums.map((v) => Math.abs(v))) : 0
    const revScale = pickRevenueChartScale(maxIncomeYuan)
    const revDiv = revScale.divisor
    const revenueScaled = rawIncome.map((v) => (Number(v) || 0) / revDiv)

    return {
        grid,
        xAxis: {
            type: 'category',
            data: xData,
            axisLabel,
            axisLine: { show: false },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            name: `收益(${revScale.unitName})`,
            nameLocation: 'end',
            nameGap: 10,
            nameTextStyle: {
                color: 'rgba(46,46,46,0.6)',
                fontSize: 10,
                padding: [0, 0, 0, 0]
            },
            axisLabel: {
                fontSize: 10,
                formatter: (val) => {
                    const n = Number(val)
                    if (!Number.isFinite(n)) return String(val)
                    if (revDiv === 1) return Math.abs(n) >= 1000 ? n.toLocaleString('zh-CN') : String(n)
                    return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                }
            },
            axisLine: { show: false },
            axisTick: { show: false }
        },
        tooltip: {
            trigger: 'axis',
            show: true,
            triggerOn: 'mousemove|click',
            confine: false,
            renderMode: 'html',
            extraCssText: 'z-index: 999999 !important;',
            axisPointer: { type: 'shadow' },
            formatter: (params) => {
                const arr = Array.isArray(params) ? params : [params]
                if (!arr.length) return ''
                const p = arr[0]
                const name = p?.axisValueLabel ?? p?.axisValue ?? ''
                const v = p?.value
                const num = Number(v)
                let display = '--'
                if (Number.isFinite(num)) {
                    if (revDiv === 1) {
                        display = Number.isInteger(num)
                            ? String(num)
                            : num.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    } else {
                        display = num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    }
                }
                const u = revScale.unitName
                return `${name}<br/>收益：${display} ${u}`
            }
        },
        series: [
            {
                name: '收益',
                type: 'bar',
                silent: false,
                data: revenueScaled,
                barWidth: 12,
                itemStyle: { color: '#3A68FF', borderRadius: [4, 4, 0, 0] }
            }
        ]
    }
})

// 电量堆叠图：与 getMonthIncomeAndPower 的 list 对齐，充=sum_positive_*，放=sum_reverse_*（含深谷）
function pickStackFieldNum(item, keys) {
    if (!item) return 0
    for (const k of keys) {
        const v = item[k]
        if (v != null && v !== '') {
            const n = Number(v)
            return Number.isFinite(n) ? n : 0
        }
    }
    return 0
}

/** 按月排序后的月度电量明细（与收益柱状图 X 轴一致） */
const electricStackList = ref([])

const ELECTRICITY_STACK_SERIES = [
    { name: '充-尖', stack: 'charge', color: '#FA3636', keys: ['sum_positive_sharp', 'sumPositiveSharp'] },
    { name: '充-峰', stack: 'charge', color: '#FA711E', keys: ['sum_positive_peak', 'sumPositivePeak'] },
    { name: '充-平', stack: 'charge', color: '#14D872', keys: ['sum_positive_flat', 'sumPositiveFlat'] },
    { name: '充-谷', stack: 'charge', color: '#1283F0', keys: ['sum_positive_valley', 'sumPositiveValley'] },
    { name: '充-深谷', stack: 'charge', color: '#EFC700', keys: ['sum_positive_deep_valley', 'sumPositiveDeepValley', 'sum_positive_deepValley'] },
    { name: '放-尖', stack: 'discharge', color: '#F4B1B6', keys: ['sum_reverse_sharp', 'sumReverseSharp'] },
    { name: '放-峰', stack: 'discharge', color: '#F4C6AE', keys: ['sum_reverse_peak', 'sumReversePeak'] },
    { name: '放-平', stack: 'discharge', color: '#A1EBCC', keys: ['sum_reverse_flat', 'sumReverseFlat'] },
    { name: '放-谷', stack: 'discharge', color: '#A1D9FF', keys: ['sum_reverse_valley', 'sumReverseValley'] },
    { name: '放-深谷', stack: 'discharge', color: '#E6D581', keys: ['sum_reverse_deep_valley', 'sumReverseDeepValley', 'sum_reverse_deepValley'] }
]

/** 各月充电柱总高、放电柱总高的峰值（kWh），用于选 Y 轴单位 */
function maxElectricMonthStackPeakKwh(list) {
    if (!Array.isArray(list) || !list.length) return 0
    let max = 0
    for (const item of list) {
        let chargeSum = 0
        let dischargeSum = 0
        for (const s of ELECTRICITY_STACK_SERIES) {
            const v = pickStackFieldNum(item, s.keys)
            if (s.stack === 'charge') chargeSum += v
            else if (s.stack === 'discharge') dischargeSum += v
        }
        max = Math.max(max, chargeSum, dischargeSum)
    }
    return max
}

const electricityStackSeriesStackByName = Object.fromEntries(
    ELECTRICITY_STACK_SERIES.map((s) => [s.name, s.stack])
)
const electricityStackSeriesColorByName = Object.fromEntries(
    ELECTRICITY_STACK_SERIES.map((s) => [s.name, s.color])
)

const electricityStackMonths = computed(() =>
    electricStackList.value.map((it) => String(it?.date ?? '--'))
)

const electricityStackSeriesMatrix = computed(() => {
    const list = electricStackList.value
    return ELECTRICITY_STACK_SERIES.map((s) => list.map((item) => pickStackFieldNum(item, s.keys)))
})

const electricityLegendChargeNames = ELECTRICITY_STACK_SERIES.filter((s) => s.stack === 'charge').map((s) => s.name)
const electricityLegendDischargeNames = ELECTRICITY_STACK_SERIES.filter((s) => s.stack === 'discharge').map((s) => s.name)

const electricityStackLegendBase = {
    type: 'scroll',
    orient: 'horizontal',
    left: 'center',
    width: '88%',
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 6,
    pageIconSize: 8,
    pageButtonGap: 2,
    pageTextStyle: { fontSize: 9 },
    textStyle: { fontSize: 9 }
}

const electricityStackOption = computed(() => {
    const hasData = Array.isArray(electricStackList.value) && electricStackList.value.length > 0

    if (!hasData) {
        return {
            grid: { top: 52, bottom: 16, left: 32, right: 16, containLabel: true },
            legend: [
                { ...electricityStackLegendBase, data: electricityLegendChargeNames, top: -4 },
                { ...electricityStackLegendBase, data: electricityLegendDischargeNames, top: 14 }
            ],
            xAxis: {
                type: 'category',
                data: yearMonthsToNow.value,
                axisLabel: { fontSize: 10 },
                axisLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.12)' } },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                axisLabel: { fontSize: 10 },
                splitLine: { show: true, lineStyle: { color: 'rgba(0,0,0,0.06)' } },
                axisTick: { show: false },
                name: '电量(kWh)',
                nameLocation: 'end',
                nameGap: 10,
            },
            tooltip: { show: false },
            series: []
        }
    }

    const peakKwh = maxElectricMonthStackPeakKwh(electricStackList.value)
    const edScale = pickChargeDischargeEnergyScale(peakKwh)
    const edDiv = edScale.divisor
    const edUnit = edScale.unit

    return {
        // 给 legend 预留空间，避免小屏时图例压到绘图区
        grid: { top: 52, bottom: 16, left: 32, right: 16, containLabel: true },
        legend: [
            { ...electricityStackLegendBase, data: electricityLegendChargeNames, top: -4 },
            { ...electricityStackLegendBase, data: electricityLegendDischargeNames, top: 14 }
        ],
        xAxis: {
            type: 'category',
            data: electricityStackMonths.value,
            axisLabel: { fontSize: 10 },
            axisLine: { show: false },
            axisTick: { show: false }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                fontSize: 10,
                formatter: (val) => {
                    const n = Number(val)
                    if (!Number.isFinite(n)) return String(val)
                    if (edDiv === 1) return Math.abs(n) >= 1000 ? n.toLocaleString('zh-CN') : String(n)
                    return n.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                }
            },
            axisLine: { show: false },
            axisTick: { show: false },
            name: `电量(${edUnit})`,
            nameLocation: 'end',
            nameGap: 10,
        },
        tooltip: {
            trigger: 'axis',
            show: true,
            triggerOn: 'mousemove|click',
            confine: false,
            renderMode: 'html',
            extraCssText: 'z-index: 999999 !important;',
            position: ['0', '10%'],
            // 两列展示：左侧充电(5项)，右侧放电(5项)，避免浮层过长
            formatter: (params) => {
                const arr = Array.isArray(params) ? params : [params]
                const axisLabel = arr?.[0]?.axisValueLabel ?? arr?.[0]?.axisValue ?? ''

                const chargeItems = []
                const dischargeItems = []

                for (const p of arr) {
                    const seriesName = p?.seriesName ?? ''
                    const stack = electricityStackSeriesStackByName[seriesName]
                    const color = electricityStackSeriesColorByName[seriesName] || 'rgba(0,0,0,0.15)'
                    const rawVal = p?.value
                    const v = Array.isArray(rawVal) ? rawVal[1] : rawVal
                    const num = Number(v)
                    const display = Number.isFinite(num) ? num.toFixed(2) : '--'

                    const rowHtml = `
                    <div style="display:flex;justify-content:space-between;gap:12px;line-height:18px;">
                        <span style="display:inline-flex;align-items:center;gap:6px;">
                            <span style="width:10px;height:10px;border-radius:2px;background:${color};display:inline-block;"></span>
                            <span>${seriesName}</span>
                        </span>
                        <span>${display} ${edUnit}</span>
                    </div>`

                    if (stack === 'charge') chargeItems.push(rowHtml)
                    else if (stack === 'discharge') dischargeItems.push(rowHtml)
                }

                const left = chargeItems.length ? chargeItems.join('') : `<div style="color:rgba(0,0,0,.45);">无</div>`
                const right = dischargeItems.length ? dischargeItems.join('') : `<div style="color:rgba(0,0,0,.45);">无</div>`

                return `
                <div style="min-width:260px;">
                    <div style="font-weight:600;margin-bottom:8px;">${axisLabel}</div>
                    <div style="display:flex;gap:16px;">
                        <div style="flex:1;">
                            <div style="font-size:12px;color:rgba(0,0,0,.55);margin-bottom:6px;">充电</div>
                            ${left}
                        </div>
                        <div style="flex:1;">
                            <div style="font-size:12px;color:rgba(0,0,0,.55);margin-bottom:6px;">放电</div>
                            ${right}
                        </div>
                    </div>
                </div>`
            }
        },
        series: ELECTRICITY_STACK_SERIES.map((s, i) => ({
            name: s.name,
            type: 'bar',
            stack: s.stack,
            silent: false,
            barWidth: 18,
            data: (electricityStackSeriesMatrix.value[i] || []).map((v) => (Number(v) || 0) / edDiv),
            itemStyle: { color: s.color },
        }))
    }
})



onMounted(async () => {
    await fetchTopMetrics()
    await refreshDataByTimeRange()
    await autoAdjustRevenuePageSize()

    if (typeof ResizeObserver !== 'undefined' && revenueTableInnerRef.value) {
        revenueTableResizeObserver = new ResizeObserver(() => {
            autoAdjustRevenuePageSize()
        })
        revenueTableResizeObserver.observe(revenueTableInnerRef.value)
    }
})

onBeforeUnmount(() => {
    if (revenueSearchDebounceTimer) {
        clearTimeout(revenueSearchDebounceTimer)
        revenueSearchDebounceTimer = null
    }
    if (revenueTableResizeObserver) {
        revenueTableResizeObserver.disconnect()
        revenueTableResizeObserver = null
    }
})


let fetchStationListReqSeq = 0
const fetchStationList = async (range, pageOverride, pageSizeOverride) => {
    const reqSeq = ++fetchStationListReqSeq
    const { startTime, endTime } = range || getIncomeQueryTimeRange(timeRange.value)
    const { name, stationId } = stationSearchNameOrId(revenueSearch.value)
    const params = {
        pageNum: pageOverride ?? revenuePage.value,
        pageSize: pageSizeOverride ?? revenuePageSize.value,
        startTime,
        endTime,
        name,
        stationId,
    }
    // 拼接筛选条件：Tab 为「全部」时不进以下分支；仅 type/area/capacity 且已选下拉值时带上参数
    if (revenueFilter.value === 'type' && revenueFilterValue.value != null) {
        params.incomeModel = revenueFilterValue.value
        params.income = revenueFilterValue.value
    }
    if (revenueFilter.value === 'area' && revenueFilterValue.value != null) {
        params.area = revenueFilterValue.value
    }
    if (revenueFilter.value === 'capacity' && revenueFilterValue.value != null) {
        const key = String(revenueFilterValue.value)
        const found = capacityFilterOptions.find(
            (o) => `${o.value.start}-${o.value.end}` === key
        )
        if (found) {
            params.startCapacity = found.value.start
            params.endCapacity = found.value.end
        }
    }
    loadingRevenueList.value = true
    try {
        const stationList = await getStationList(params)
        if (reqSeq !== fetchStationListReqSeq) return
        if (stationList?.data && Array.isArray(stationList.data.list)) {
            revenueList.value = stationList.data.list.map((row) => {
                const cmp = buildYesterdayVsLastMonthTip(row.yesterdayIncome, row.avgMonthIncome)
                const effCmp = buildEfficiencyVsLastMonthTip(
                    row.yesterdayPositive,
                    row.yesterdayReverse,
                    row.avgMonthPositive,
                    row.avgMonthReverse
                )
                const effUpdate = effCmp ? { effTip: effCmp.tip, effTipDir: effCmp.dir } : {}
                const incomeUpdate = cmp ? { incomeTip: cmp.tip, incomeTipDir: cmp.dir } : {}
                return {
                    ...row,
                    ...incomeUpdate,
                    ...effUpdate
                }
            })
            revenueTotal.value = stationList.data.total
        } else {
            revenueList.value = []
            revenueTotal.value = 0
        }
    } finally {
        if (reqSeq === fetchStationListReqSeq) {
            loadingRevenueList.value = false
        }
    }
}


const efficiencyData = ref({})
// 收益/电量等：按“本年-月维度”展示（参考 workbench/OP/index.vue）
const fetchIncomAndPower = async (range) => {
    loadingBasicData.value = true
    loadingChartData.value = true
    try {
        const TIME_FMT = 'YYYY-MM-DD HH:mm:ss'
        const startTime = dayjs().startOf('year').format(TIME_FMT)
        const endTime = dayjs().format(TIME_FMT)
        const res = await getMonthIncomeAndPower({ startTime, endTime, type: 'month' })
        efficiencyData.value = {}
        revenueBarData.value = []
        revenueXDate.value = []
        electricStackList.value = []
        if (res?.code === 200 && res?.data && res?.data?.efficiency) {
            efficiencyData.value = Object.assign(efficiencyData.value, res.data.efficiency)
        }
        if (res?.code === 200 && res?.data?.income) {
            revenueDonutData.value = buildRevenueDonutFromIncome(res.data.income)
        }
        if (res?.code === 200 && Array.isArray(res?.data?.list)) {
            const sorted = [...res.data.list].sort((a, b) =>
                String(a?.date ?? '').localeCompare(String(b?.date ?? ''))
            )
            electricStackList.value = sorted
            sorted.forEach((item) => {
                revenueXDate.value.push(item.date)
                revenueBarData.value.push(item.sum_income)
            })
        }
        if (res?.code === 200 && res?.data) {
            basicCards.value.forEach(item => {
                if (item.key === 'yesterdayIncomeTotal' || item.key === 'sum_income') {
                    item.value = formatMoney(res.data[item.key]).value
                    item.unit = formatMoney(res.data[item.key]).unit
                } else {
                    item.value = formatNumber(res.data[item.key]).value
                    item.unit = formatNumber(res.data[item.key]).unit
                }
            })
        }
    } finally {
        loadingBasicData.value = false
        loadingChartData.value = false
    }
}

// 获取顶部数据
async function fetchTopMetrics() {
    loadingTopMetrics.value = true
    try {
        const topData = await getStationStatistics()
        if (topData.code === 200 && topData.data) {
            topMetrics.value = [
                { key: 'stations', label: '总电站数据', value: topData.data.total || 0, unit: '个' },
                { key: 'gridEnergy', label: '累计装机容量', value: topData.data.capacity ? formatNumber(topData.data.capacity).value : 0, unit: topData.data.capacity ? formatNumber(topData.data.capacity).unit : '' },
                { key: 'days', label: '安全运行天数', value: topData.data.days || 0, unit: '天' }
            ]
        }
    } finally {
        loadingTopMetrics.value = false
    }
}

function formatPowerNumberMW(num) {
    if (num == null || isNaN(Number(num))) {
        return { value: '0.00', unit: 'MW' }
    }
    const n = Number(num)
    const value = n / 1000
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit: 'MW'
    }
}

function formatNumberMW(num) {
    if (num == null || isNaN(Number(num))) {
        return { value: '0.00', unit: 'MWh' }
    }
    const n = Number(num)
    const value = n / 1000
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit: 'MWh'
    }
}

// 换算电量单位
function formatNumber(num) {
    if (num == null || isNaN(Number(num))) {
        return { value: '0.00', unit: 'kWh' }
    }
    const n = Number(num)
    let value = n
    let unit = 'kWh'
    if (n >= 1_000_000) {
        value = n / 1_000_000
        unit = 'GWh'
    } else if (n >= 1_000) {
        value = n / 1_000
        unit = 'MWh'
    }
    return {
        value: value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2, }),
        unit: unit
    }
}

// 换算收益单位
function formatMoney(num) {
    const n = Number(num ?? 0);
    if (isNaN(n)) return { value: '0.00', unit: '元' };

    const units = [
        { threshold: 100000000, divisor: 100000000, unit: '亿元' },
        { threshold: 10000, divisor: 10000, unit: '万元' }
    ];

    const { divisor, unit } = units.find(u => n >= u.threshold) || { divisor: 1, unit: '元' };
    return {
        value: (n / divisor).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        unit
    };
}

/**
 * 宽松数字解析：兼容 `1,579.1`、`12.3元/kWh`、`{...}` 这类后端返回格式
 * 解析失败返回 null，避免 `Number('1,579.1')` → NaN 导致 tip 被隐藏
 */
function parseLooseNumber(v) {
    if (v == null) return null
    if (typeof v === 'number') return Number.isFinite(v) ? v : null
    if (typeof v === 'string') {
        const s = v.trim()
        if (!s || s === '--' || s.toLowerCase() === 'null') return null
        const cleaned = s.replace(/,/g, '')
        const m = cleaned.match(/-?\d+(?:\.\d+)?(?:e[-+]?\d+)?/i)
        if (!m) return null
        const n = Number(m[0])
        return Number.isFinite(n) ? n : null
    }
    if (typeof v === 'object') {
        if ('value' in v) return parseLooseNumber(v.value)
        if ('amount' in v) return parseLooseNumber(v.amount)
    }
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

/**
 * 计算“较上月增加/减少XX%”
 * - yesterdayIncome：昨日收益
 * - lastMonthIncome：上月收益
 */
function buildYesterdayVsLastMonthTip(yesterdayIncome, lastMonthIncome) {
    const y = parseLooseNumber(yesterdayIncome)
    const l = parseLooseNumber(lastMonthIncome)
    if (y == null || l == null) return null
    if (l === 0) {
        if (y === 0) return null
        return { tip: '较上月均值增加（上月为0）', dir: 'up' }
    }
    const pct = (y - l) / l
    if (!Number.isFinite(pct) || pct === 0) return null
    // 避免四舍五入导致出现“0%”（例如 0.4% → 0%），只要有变化至少展示 1%
    const absPct = Math.max(1, Math.ceil(Math.abs(pct * 100)))
    if (pct > 0) return { tip: `较上月均值增加${absPct}%`, dir: 'up' }
    return { tip: `较上月均值减少${absPct}%`, dir: 'down' }
}

function calcEfficiencyNum(positive, reverse) {
    const p = parseLooseNumber(positive)
    const r = parseLooseNumber(reverse)
    if (p == null || r == null) return null
    // 正向有功为 0：两向为 0 视为 0%，否则无法定义效率
    if (p === 0) return r === 0 ? 0 : null
    return (r / p) * 100
}

/** 与 fetchPower 一致：仅在 (85%, 95%) 内展示数值百分比 */
function isEfficiencyDisplayablePercent(eff) {
    return eff != null && Number.isFinite(eff) && eff > 85 && eff < 95
}

/**
 * 昨日效率 vs 上月效率对比
 * efficiency = 反向有功 / 正向有功 * 100(%)
 */
function buildEfficiencyVsLastMonthTip(yesterdayPositive, yesterdayReverse, lastMonthPositive, lastMonthReverse) {
    const yEff = calcEfficiencyNum(yesterdayPositive, yesterdayReverse)
    const lEff = calcEfficiencyNum(lastMonthPositive, lastMonthReverse)
    if (yEff == null || lEff == null) return null
    if (!isEfficiencyDisplayablePercent(yEff) || !isEfficiencyDisplayablePercent(lEff)) return null

    const pct = (yEff - lEff) / lEff
    if (!Number.isFinite(pct) || pct === 0) return null
    // 避免四舍五入导致出现“0%”，只要有变化至少展示 1%
    const absPct = Math.max(1, Math.ceil(Math.abs(pct * 100)))
    if (pct > 0) return { tip: `较上月均值增加${absPct}%`, dir: 'up' }
    return { tip: `较上月均值减少${absPct}%`, dir: 'down' }
}

function fetchPower(Positive, Reverse) {
    if (!Positive || !Reverse || isNaN(Number(Positive)) || isNaN(Number(Reverse)) || Number(Positive) === 0) {
        return '--';
    }
    const efficiency = (Number(Reverse) / Number(Positive)) * 100;
    if (!Number.isFinite(efficiency) || efficiency <= 85 || efficiency >= 95) {
        return '--';
    }
    return efficiency.toFixed(1) + '%';
}

function fetchCycleCount(Positive, capacity) {
    if (!Positive || !capacity || isNaN(Number(Positive)) || isNaN(Number(capacity)) || Number(capacity) === 0) {
        return '0.0';
    }
    const result = Number(Positive) / Number(capacity);
    return result.toFixed(1);

}
</script>

<style lang="scss" scoped>
.ops-page {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

/* ----- 顶部三栏 ----- */
.ops-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    padding: 0 16px 0 0;
    margin-bottom: 16px;
}

/* 左：欢迎 + 副标题 + 时间 Tab（纵向排列） */
.ops-top-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ops-welcome-title {
    font-size: 28px;
    font-weight: 700;
    color: #2e2e2e;
    line-height: 1.2;
}

.ops-welcome-subtitle {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.6);
}

.ops-top-left .ops-tabs {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    padding: 4px;
    margin-bottom: 0;
    border-radius: 999px;
    background: #F3F6FF;
    box-shadow: 0 4px 12px #3a68ff14;

}

.ops-tab {
    min-width: 68px;
    text-align: center;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 14px;
    color: rgba(46, 46, 46, 0.65);
    cursor: pointer;
    transition: all 0.2s;
}

.ops-tab.active {
    background: #3A68FF;
    color: #fff;
    box-shadow: 0 6px 16px rgba(58, 104, 255, 0.35);
}

/* 中：KPI（标签在上、数值在下，浅灰细体） */
.ops-top-center {
    display: flex;
    align-items: center;
    gap: 48px;
    flex: 1;
    justify-content: center;
    min-width: 0;
}

.ops-kpi {
    text-align: center;
}

.ops-kpi-label {
    font-size: 13px;
    color: rgba(46, 46, 46, 0.6);
    margin-bottom: 4px;
}

.ops-kpi-value {
    font-size: 28px;
    font-weight: 300;
    color: rgba(46, 46, 46, 0.75);
}

/* 右：6 个快捷入口（白底圆 + 彩色图标 + 文案） */
.ops-top-right {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-shrink: 0;
}

.ops-quick-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    width: 64px;
}

.ops-quick-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: box-shadow 0.2s;
}

.ops-quick-icon:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.ops-quick-icon img {
    width: 28px;
    height: 28px;
    object-fit: contain;
}

.ops-quick-label {
    margin-top: 8px;
    font-size: 13px;
    color: #2e2e2e;
    text-align: center;
    white-space: nowrap;
}

.ops-content {
    flex: 1;
    min-height: 0;
    // padding: 0 16px 16px 0;
    margin-top: 16px;
    overflow-y: auto;
}

.ops-row {
    margin: 0;
    align-items: stretch;
    min-width: 0;
}

.ops-row :deep(.v-col) {
    min-width: 0;
}

.ops-col-left,
.ops-col-right {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-width: 0;
}

/* 左右两列之间缝隙稍微加大（不改变栅格占比，避免被挤换行） */
.ops-col-left {
    padding-right: 4px;
}

.ops-col-right {
    padding-left: 10px;
}

.ops-basic-bg {
    padding: 10px 24px;
    border-radius: 20px;
    border: 0.5px solid #FFF;
    background: rgba(255, 255, 255, 0.50);
    backdrop-filter: blur(10px);
}

/* 基础数据统计：设计图卡片容器 */
.ops-basic-section {
    padding: 10px 24px;
    border-radius: 20px;
    border: 0.5px solid #FFF;
    background: rgba(255, 255, 255, 0.50);
    backdrop-filter: blur(10px);
}

.ops-basic-title {
    margin: 0 0 20px 0;
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
}

.ops-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 16px 24px;
    margin-left: clamp(0px, 4vw, 70px);
}

@media (max-width: 600px) {
    .ops-cards {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

.ops-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    // gap: 8px;
}

.ops-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #3A68FF 0%, #6B8CFF 50%, #9B7BDB 100%);
    box-shadow: 0 4px 12px rgba(58, 104, 255, 0.35);
}

.ops-card-icon-cube {
    width: 20px;
    height: 20px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.5), 0 2px 4px rgba(0, 0, 0, 0.15);
}

.ops-card-label {
    max-width: 100%;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.65);
    overflow-wrap: anywhere;
}

.ops-card-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.ops-card-value {
    font-size: 22px;
    font-weight: 700;
    color: #2E2E2E;
}

.ops-card-unit {
    font-size: 13px;
    color: rgba(46, 46, 46, 0.6);
}

/* ----- 收益列表模块（设计图） ----- */
.ops-revenue-panel {
    border: 1px solid rgba(0, 0, 0, 0.06);
    height: 100%;
    // background: #fff;
    // padding: 20px 24px 24px;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.ops-revenue-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.ops-revenue-header .ops-panel-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
}

.ops-revenue-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px 16px;
    flex: 1 1 420px;
    min-width: 0;
    flex-wrap: wrap;
}

.ops-revenue-toolbar .ops-search {
    width: 220px;
    max-width: 100%;
    flex: 0 1 220px;
}

.ops-revenue-toolbar .ops-search :deep(.el-input__wrapper) {
    border-radius: 999px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
    background: rgba(255, 255, 255, 0.9);
    padding: 0 12px;
}

.ops-revenue-toolbar .ops-search :deep(.el-input__inner) {
    font-size: 13px;
}

.ops-revenue-toolbar .ops-search :deep(.el-input__suffix) {
    color: rgba(46, 46, 46, 0.35);
}

.ops-revenue-toolbar .ops-filter-tabs {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    margin-bottom: 0;
    border-radius: 999px;
    background: #F3F6FF;
    box-shadow: 0 4px 12px rgba(58, 104, 255, 0.08);
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
}

.ops-revenue-toolbar .ops-filter-tabs::-webkit-scrollbar {
    display: none;
}

/* 与左侧「搜索电站」同圆角/描边/背景，宽度收窄（避免 el-select 默认 min-width 撑开） */
.ops-filter-dropdown {
    width: 132px;
    max-width: 132px;
    flex-shrink: 0;
}

.ops-filter-dropdown :deep(.el-select__wrapper),
.ops-filter-dropdown :deep(.el-input__wrapper) {
    border-radius: 999px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
    background: rgba(255, 255, 255, 0.9);
    padding: 0 12px;
    min-height: 32px;
    min-width: 0;
}

.ops-filter-dropdown :deep(.el-select__selection) {
    font-size: 13px;
}

.ops-filter-dropdown :deep(.el-select__placeholder) {
    font-size: 13px;
    color: rgba(46, 46, 46, 0.45);
}

.ops-filter-dropdown :deep(.el-select__selected-item) {
    font-size: 13px;
}

.ops-filter-dropdown :deep(.el-select__suffix),
.ops-filter-dropdown :deep(.el-select__caret) {
    color: rgba(46, 46, 46, 0.35);
}

.ops-filter-tab {
    min-width: 72px;
    text-align: center;
    padding: 6px 18px;
    border-radius: 999px;
    font-size: 13px;
    color: rgba(46, 46, 46, 0.7);
    cursor: pointer;
    transition: all 0.2s;
}

.ops-filter-tab.active {
    background: #3A68FF;
    color: #fff;
    box-shadow: 0 6px 16px rgba(58, 104, 255, 0.35);
}

.ops-history-link {
    margin-left: auto;
    font-size: 13px;
    color: #2e2e2e;
}

// 表格容器保留默认背景，由卡片控制圆角和边框
.ops-table-wrap {
    /* 由卡片控制整体圆角和背景，这里仅占位，避免额外边框 */
    margin-top: 8px;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
}

/* v-loading 遮罩改为透明，只保留 spinner，全页统一 */
:deep(.el-loading-mask) {
    background-color: transparent !important;
}

.ops-revenue-table-inner {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-width: 100%;
}

.ops-revenue-table {
    font-size: 13px;
    flex: 1;
    min-height: 0;
    background: transparent !important;
}

.ops-revenue-table :deep(.v-table__wrapper) {
    overflow: auto;
    background: transparent !important;
}

.ops-revenue-table :deep(table) {
    border-collapse: collapse;
    width: 100%;
    min-width: 1120px;
    table-layout: fixed;
    background: transparent;
}

.ops-revenue-table :deep(thead th),
.ops-revenue-table :deep(tbody td) {
    padding: 10px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    vertical-align: middle;
}

.ops-revenue-table :deep(thead th) {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(46, 46, 46, 0.85);
    font-weight: 500;
    white-space: nowrap;
}

.ops-revenue-table :deep(tbody tr:hover td) {
    background: rgba(230, 243, 255, 0.2);
}

.ops-revenue-pagination {
    margin-top: auto;
    position: sticky;
    bottom: 0;
    z-index: 2;
}

.ops-station-cell {
    max-width: 200px;
    min-width: 0;
    vertical-align: top;
}

.ops-station-name {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    max-width: 100%;
}

.ops-station-link {
    display: block;
    color: #3A68FF;
    text-decoration: none;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.ops-station-link:hover {
    text-decoration: underline;
}

.ops-station-spec {
    font-size: 12px;
    color: rgba(46, 46, 46, 0.55);
}

.ops-charge-discharge {
    font-size: 13px;
    color: #2e2e2e;
    line-height: 1.4;
}

.ops-tip {
    display: inline-block;
    font-size: 12px;
    color: #00C853;
    margin-left: 6px;
}

.ops-tip-down {
    color: #FF4D4F;
}

.ops-tip-down :deep(.ops-tip-arrow),
.ops-tip-down .ops-tip-arrow {
    border-bottom: 0;
    border-top: 5px solid #FF4D4F;
}

.ops-tip-arrow {
    display: inline-block;
    margin-right: 2px;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 5px solid #00C853;
    vertical-align: middle;
}

.ops-tags-wrap {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 6px;
}

.ops-device-tags {
    // padding: 4px 8px;
    // background: rgba(0, 0, 0, 0.04);
    border-radius: 6px;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 4px 6px;
}

.ops-tag {
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 12px;
}

.ops-tag.income-blue {
    background: rgba(58, 104, 255, 0.12);
    color: #3A68FF;
}

.ops-tag.income-green {
    background: rgba(0, 200, 83, 0.12);
    color: #00C853;
}

.ops-tag.income-orange {
    background: rgba(255, 152, 0, 0.15);
    color: #F57C00;
}

.ops-tag-device {
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 12px;
}

.ops-tag-device.device-storage {
    color: #3A68FF;
    border: 1px solid #3A68FF;
}

.ops-tag-device.device-pv {
    color: #00C853;
    border: 1px solid #00C853;
}

.ops-detail-link {
    font-size: 13px;
    color: #3A68FF !important;
}

.ops-detail-link:hover {
    color: #6B8CFF !important;
}

.ops-pagination {
    display: flex;
    justify-content: end;
    margin-top: 16px;
}

.ops-pagination :deep(.el-pagination) {
    justify-content: end;
}

/* 收益列表：参考 ChargeRecord.vue 的分页样式 */
.pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 24px;
    background: transparent;
    flex-shrink: 0;
    border-top: 1px solid #f0f0f0;
    width: 100%;
    flex-wrap: wrap;
}

.pagination-info {
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
}

.pagination-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 16px;
    height: 100%;
    min-width: 0;
    flex: 1 1 auto;
    flex-wrap: wrap;
}

.page-size-select {
    width: 112px;
    flex-shrink: 0;

    :deep(.v-field) {
        background: #fff !important;
        border: 1px solid #dcdcdc !important;
        border-radius: 3px !important;
        min-height: auto !important;
        height: auto !important;
        padding: 5px 8px !important;
        box-shadow: none !important;
        display: flex !important;
        align-items: center !important;
    }

    :deep(.v-field__input) {
        padding: 0 !important;
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        color: rgba(0, 0, 0, 0.9) !important;
        min-height: auto !important;
        height: 22px !important;
        line-height: 22px !important;
        display: flex !important;
        align-items: center !important;
    }

    :deep(.v-select__selection) {
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        color: rgba(0, 0, 0, 0.9) !important;
        line-height: 22px !important;
        margin: 0 !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    :deep(.v-field__outline) {
        display: none !important;
    }

    :deep(.v-field__append-inner .v-icon) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
        color: #2E2E2E !important;
        opacity: 0.22;
    }
}

.custom-pagination {
    flex-shrink: 0;

    :deep(.v-pagination__list) {
        align-items: center;
        gap: 8px !important;
        margin: 0 !important;
    }

    :deep(.v-btn) {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        border-radius: 3px !important;
        margin: 0 !important;
        font-family: 'PingFang SC', sans-serif !important;
        font-size: 14px !important;
        font-weight: 400 !important;
        line-height: 22px !important;
        border: none !important;
        background: transparent !important;
        color: #000000E6 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 0 !important;
        transition: none !important;
        box-shadow: none !important;
        text-align: center !important;

        &:hover {
            background: transparent !important;
            color: #000000E6 !important;
        }

        &.v-pagination__item--is-active {
            color: #000000E6 !important;
            border: none !important;
            box-shadow: none !important;
        }
    }

    :deep(.v-pagination__prev),
    :deep(.v-pagination__next) {
        min-width: 32px !important;
        width: 32px !important;
        height: 32px !important;
        border-radius: 3px !important;
        border: none !important;
        background: transparent !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        transition: none !important;
        box-shadow: none !important;

        &:hover:not(.v-pagination__item--disabled) {
            background: transparent !important;
        }

        &.v-pagination__item--disabled {
            opacity: 0.4 !important;
            cursor: not-allowed !important;
        }
    }

    :deep(.v-pagination__prev .v-icon),
    :deep(.v-pagination__next .v-icon) {
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
        color: rgba(0, 0, 0, 1) !important;
    }
}

.jump-to-page {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'PingFang SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.6);
    background: #e3e3e3;
    padding: 2px 16px;
    border-radius: 3px;
    line-height: 22px;
    height: 34px;
    width: 160px;
    flex-shrink: 0;

    span {
        white-space: nowrap;
        line-height: 22px;
        font-weight: 400;
        font-size: 14px;
        color: rgba(0, 0, 0, 0.6);
    }

    .jump-input {
        flex: 1 0 0;
        min-width: 0;

        :deep(.v-field) {
            background: #fff !important;
            border: 1px solid #dcdcdc !important;
            border-radius: 3px !important;
            min-height: auto !important;
            height: auto !important;
            margin: 0 !important;
            box-shadow: none !important;
            display: flex !important;
            align-items: center !important;
        }
    }
}

.ops-right-card {
    padding: 16px;
    // margin-bottom: 16px;
}

.ops-right-title {
    font-size: 15px;
    font-weight: 600;
    color: #2e2e2e;
    margin-bottom: 12px;
}

/* 效率：三段横条 + 上方气泡 + 下方数量（高还原） */
.ops-efficiency-bars {
    display: flex;
    flex-direction: column;
    gap: 0;
    margin: 20px 0px;
}

.ops-eff-bar {
    width: 100%;
}

.ops-eff-track {
    position: relative;
    padding: 10px 6px 0;
    height: 32px;
}

.ops-eff-bubble {
    position: absolute;
    top: -28px;
    transform: translateX(-50%);
    min-width: 46px;
    padding: 2px 8px;
    border-radius: 12px;
    background: #E4EBFF;
    color: #3A68FF;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    box-shadow: 0 4px 10px rgba(58, 104, 255, 0.25);
}

.ops-eff-fill {
    width: 100%;
    height: 14px;
    border-radius: 999px;
}

.ops-eff-bg {
    position: absolute;
    left: 6px;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    height: 25px;
    border-radius: 999px;
    background: #E0E6FF;
}

.ops-eff-colors {
    position: absolute;
    left: 6px;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    height: 14px;
    display: flex;
    overflow: hidden;
    border-radius: 999px;
    padding: 0px 5px;
}

.ops-eff-bubble::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: -2px;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #E4EBFF;
}

.ops-eff-bubble-left {
    left: calc(45px + (10 / 45 * 100%));
}

.ops-eff-bubble-right {
    left: calc(-94px + (40 / 45 * 100%));
}

.ops-eff-count-row {
    display: flex;
    justify-content: space-around;
    margin-top: 10px;
    padding: 0 6px;
    font-size: 13px;
    color: #2E2E2E;
}

.ops-eff-fill.yellow {
    background: #FFB74D;
}

.ops-eff-fill.green {
    background: #00C853;
}

.ops-eff-fill.blue {
    background: #3A68FF;
}

.ops-eff-count {
    font-size: 13px;
    color: #2E2E2E;
    text-align: center;
}

/* 收益分成：参考 OP 告警环形图，缩小等比例样式 */
.ops-donut-row {
    display: flex;
    align-items: center;
    gap: 16px;
}

.ops-donut-wrap {
    position: relative;
    width: 180px;
    height: 180px;
    flex-shrink: 0;
}

.ops-donut-chart {
    width: 100%;
    height: 100%;
}

.ops-donut-center {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 13px;
    font-weight: 500;
    color: #2e2e2e;
}

.ops-donut-legend {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    color: rgba(46, 46, 46, 0.85);
}

.ops-donut-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-radius: 14px;
    background: rgba(0, 0, 0, 0.04);
}

.ops-donut-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-right: 6px;
}

.ops-donut-name {
    flex: 1;
}

.ops-donut-count,
.ops-donut-pct {
    margin-left: 8px;
}

/* 收益 / 电量图表区域 */
.ops-chart-box {
    height: 249px;
    border-radius: 8px;
    margin-top: 14px;
    overflow: visible;
    position: relative;
}

.ops-chart {
    width: 100%;
    height: 100%;
}

:deep(.echarts-tooltip),
:deep(.echarts-tooltip-dark),
:deep(.echarts-tooltip-light) {
    z-index: 999999 !important;
}

@media (max-width: 1400px),
(max-height: 800px) {
    .ops-donut-row {
        align-items: stretch;
        flex-direction: column;
        overflow: visible;
    }

    .ops-donut-wrap {
        align-self: center;
        flex: 0 0 150px;
        width: 150px;
        height: 150px;
        margin-top: 10px;
    }

    .ops-donut-legend {
        flex: 0 0 auto;
        padding: 0 24px 12px 12px;
    }

    .ops-donut-item {
        padding: 6px 8px;
        gap: 4px;
        font-size: 11px;
    }

    .ops-donut-dot {
        width: 6px;
        height: 6px;
    }

    .ops-donut-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .ops-donut-count {
        flex: 0 0 54px;
        width: 54px;
        min-width: 54px;
        margin-left: 0;
        text-align: right;
    }

    .ops-donut-pct {
        flex: 0 0 48px;
        width: 48px;
        min-width: 48px;
        margin-left: 0;
        text-align: right;
    }
}

@media (min-width: 1401px) and (max-width: 1919px) {
    .ops-donut-row {
        align-items: stretch;
        flex-direction: column;
        overflow: visible;
    }

    .ops-donut-wrap {
        align-self: center;
        flex: 0 0 155px;
        width: 155px;
        height: 155px;
        margin-top: 8px;
    }

    .ops-donut-legend {
        flex: 0 0 auto;
        padding: 0 24px 12px 14px;
    }

    .ops-donut-item {
        padding: 7px 10px;
        gap: 6px;
        font-size: 11px;
    }

    .ops-donut-dot {
        width: 6px;
        height: 6px;
    }

    .ops-donut-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .ops-donut-count {
        flex: 0 0 54px;
        width: 54px;
        min-width: 54px;
        margin-left: 0;
        text-align: right;
    }

    .ops-donut-pct {
        flex: 0 0 48px;
        width: 48px;
        min-width: 48px;
        margin-left: 0;
        text-align: right;
    }
}

@media (max-width: 1200px) {
    .ops-revenue-table :deep(table) {
        min-width: 1120px;
    }
}

@media (max-width: 960px) {

    .ops-col-left,
    .ops-col-right {
        padding-left: 0;
        padding-right: 0;
    }

    .ops-revenue-toolbar {
        justify-content: flex-start;
        flex-basis: 100%;
    }

    .ops-revenue-toolbar .ops-filter-tabs {
        width: 100%;
    }
}

@media (max-width: 600px) {

    .ops-basic-bg,
    .ops-basic-section,
    .ops-right-card {
        padding-left: 14px;
        padding-right: 14px;
    }

    .ops-filter-tab {
        min-width: 64px;
        padding-left: 12px;
        padding-right: 12px;
    }

    .pagination-container {
        padding-left: 12px;
        padding-right: 12px;
    }

    .pagination-info,
    .pagination-controls {
        flex-basis: 100%;
        justify-content: center;
    }
}
</style>
